import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeGetAttachments} from '../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {CommentModel} from '../../../models/api/commentModel';
import {AppState} from '../../../store/appState';
import {getConversations} from '../../../store/data/dataSelectors';
import {getFindTeammate} from '../../../store/data/teammates/lazyTeammatesSelectors';
import {getAllAliases, getMentionableAliases} from '../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserAlias, getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {getHasQuoteComment} from '../../../store/featureFlags/featureFlagsSelectors';
import {NewPanelId, RViewerPanelId} from '../../../store/panels/panelStateBase';
import {jumpToActivityAction} from '../../../store/panels/viewer/viewerPanelActions';
import {FloatingCommentDropdown} from './floatingCommentDropdown';

/*
 * Props.
 */

interface FloatingCommentDropdownContainerProps {
  panelId: NewPanelId;
  comment: CommentModel;
}

/*
 * Selectors.
 */

function getComment(_state: AppState, props: FloatingCommentDropdownContainerProps) {
  return props.comment;
}

function makeGetAuthor() {
  return createSelector([getComment, getFindTeammate], (comment, findTeammate) => {
    if (!comment) {
      return undefined;
    }

    return findTeammate(comment.authorId, 'FloatingCommentDropdownContainer makeGetAuthor');
  });
}

function makeGetConversationId() {
  return createSelector(getComment, (comment) => comment.conversationId);
}

function makeGetNamespace(getConversationId: ReturnType<typeof makeGetConversationId>) {
  return createSelector([getConversationId, getConversations], (conversationId, conversations) =>
    conversationId ? conversations[conversationId]?.namespace : undefined,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<FloatingCommentDropdownContainerProps>()(() => {
  const getConversationId = makeGetConversationId();
  const getNamespace = makeGetNamespace(getConversationId);

  return {
    comment: getComment,
    author: makeGetAuthor(),
    userId: getUserId,
    attachments: makeGetAttachments(getComment),
    allAliases: getAllAliases,
    mentionableAliases: getMentionableAliases,
    userAlias: getUserAlias,
    conversationId: getConversationId,
    namespace: getNamespace,
    hasQuoteComment: getHasQuoteComment,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<FloatingCommentDropdownContainerProps>()(() => ({
  jumpToActivity: jumpToActivityAction,
}));

export const FloatingCommentDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {panelId} = ownProps;

    const onJumpCommentClick = () => {
      // TODO: Fix the behavior of annotations in popup message viewers.
      if (!RViewerPanelId.is(panelId)) {
        return;
      }

      const activityId = stateProps.comment.annotation?.commentActivityId;

      if (!activityId) {
        return;
      }

      dispatchProps.jumpToActivity(panelId, {id: activityId});
    };

    return {
      ...ownProps,
      ...stateProps,
      onJumpCommentClick,
    };
  },
)(FloatingCommentDropdown);
